import React, { useMemo, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import type { ID, OrgChartNode } from '@hrms/shared';
import { Avatar, Badge, Button, Card, Icon, Row, Text, TextField, radius, useTheme } from '../../../ui';

const countAll = (n: OrgChartNode): number => n.children.reduce((a, c) => a + 1 + countAll(c), 0);

/**
 * Interactive org chart as an expandable outline tree (reads well on phones and desktop).
 * Solid guides = reporting line; a dashed "Dotted line" tag names the dotted-line manager.
 */
export function OrgChart({ roots, onSelect, initialDepth = 2 }: { roots: OrgChartNode[]; onSelect?: (id: ID) => void; initialDepth?: number }) {
  const { colors } = useTheme();
  const { byId, parents } = useMemo(() => {
    const m = new Map<ID, OrgChartNode>();
    const p = new Map<ID, ID>();
    const walk = (n: OrgChartNode) => {
      m.set(n.id, n);
      n.children.forEach((c) => {
        p.set(c.id, n.id);
        walk(c);
      });
    };
    roots.forEach(walk);
    return { byId: m, parents: p };
  }, [roots]);
  const [expanded, setExpanded] = useState<Set<ID>>(() => {
    const s = new Set<ID>();
    const walk = (n: OrgChartNode, d: number) => {
      if (d < initialDepth) {
        s.add(n.id);
        n.children.forEach((c) => walk(c, d + 1));
      }
    };
    roots.forEach((r) => walk(r, 0));
    return s;
  });
  const [q, setQ] = useState('');
  const [hl, setHl] = useState<ID | null>(null);

  const matches = useMemo(() => {
    const s = new Set<ID>();
    if (q.trim().length < 2) return s;
    byId.forEach((n) => n.name.toLowerCase().includes(q.trim().toLowerCase()) && s.add(n.id));
    return s;
  }, [q, byId]);

  const reveal = (ids: Iterable<ID>) => {
    const next = new Set(expanded);
    for (const id of ids) {
      let p = parents.get(id);
      while (p) {
        next.add(p);
        p = parents.get(p);
      }
    }
    setExpanded(next);
  };
  const toggle = (id: ID) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const render = (n: OrgChartNode, depth: number): React.ReactNode => {
    const open = expanded.has(n.id);
    const dotted = n.dotted_manager_id ? byId.get(n.dotted_manager_id) : null;
    const isMatch = matches.has(n.id) || hl === n.id;
    return (
      <View key={n.id}>
        <View style={{ flexDirection: 'row', alignItems: 'stretch' }}>
          {Array.from({ length: depth }).map((_, i) => (
            <View key={i} style={{ width: 20, alignItems: 'center' }}>
              <View style={{ width: 1.5, flex: 1, backgroundColor: colors.borderStrong }} />
            </View>
          ))}
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 4 }}>
            {n.children.length > 0 ? (
              <Pressable accessibilityRole="button" accessibilityLabel={`${open ? 'Collapse' : 'Expand'} ${n.name}'s reports`} accessibilityState={{ expanded: open }} onPress={() => toggle(n.id)} style={{ width: 24, height: 24, alignItems: 'center', justifyContent: 'center', borderRadius: 12, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface }}>
                <Icon name={open ? 'chevronDown' : 'chevronRight'} size={13} color={colors.muted} />
              </Pressable>
            ) : (
              <View style={{ width: 24, alignItems: 'center' }}>
                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: colors.borderStrong }} />
              </View>
            )}
            <Pressable
              disabled={!onSelect}
              onPress={() => onSelect?.(n.id)}
              accessibilityRole={onSelect && Platform.OS !== 'web' ? 'button' : undefined}
              focusable={!!onSelect}
              accessibilityLabel={`${n.name}, ${n.designation ?? ''}`}
              style={({ pressed }) => ({
                flex: 1,
                maxWidth: 520,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                padding: 8,
                borderRadius: radius.md,
                borderWidth: 1,
                borderColor: isMatch ? colors.primary : colors.border,
                backgroundColor: pressed ? colors.hover : isMatch ? colors.primarySoft : colors.surface,
              })}
            >
              <Avatar name={n.name} size={32} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text weight="semibold" numberOfLines={1}>
                  {n.name}
                </Text>
                <Text variant="small" numberOfLines={1}>
                  {[n.designation, n.department].filter(Boolean).join(' · ') || '—'}
                </Text>
                {!!n.dotted_manager_id && (
                  <Pressable onPress={() => { setHl(n.dotted_manager_id); if (n.dotted_manager_id) reveal([n.dotted_manager_id]); }} accessibilityRole="button" accessibilityLabel={`Dotted-line manager ${dotted?.name ?? n.dotted_manager_id}`} style={{ marginTop: 4, alignSelf: 'flex-start', borderWidth: 1, borderStyle: 'dashed', borderColor: colors.info, borderRadius: radius.sm, paddingHorizontal: 6, paddingVertical: 1 }}>
                    <Text size={11} color={colors.info}>
                      ┄ Dotted line: {dotted?.name ?? `#${n.dotted_manager_id}`}
                    </Text>
                  </Pressable>
                )}
              </View>
              {n.children.length > 0 && <Badge tone="neutral">{`${n.children.length} · ${countAll(n)}`}</Badge>}
            </Pressable>
          </View>
        </View>
        {open && n.children.map((c) => render(c, depth + 1))}
      </View>
    );
  };

  return (
    <Card testID="org-chart">
      <Row gap={8} style={{ marginBottom: 12 }}>
        <TextField containerStyle={{ flex: 1, minWidth: 180, maxWidth: 280 }} placeholder="Find a person…" accessibilityLabel="Find in org chart" value={q} onChangeText={setQ} onSubmitEditing={() => reveal(matches)} />
        <Button size="sm" title={`Reveal ${matches.size || ''} match${matches.size === 1 ? '' : 'es'}`} disabled={!matches.size} onPress={() => reveal(matches)} />
        <Button size="sm" variant="ghost" title="Expand all" onPress={() => setExpanded(new Set(byId.keys()))} />
        <Button size="sm" variant="ghost" title="Collapse all" onPress={() => { setExpanded(new Set()); setHl(null); }} />
      </Row>
      <Text variant="small" style={{ marginBottom: 8 }}>
        Badge shows direct · total reports. Tap a dotted-line tag to highlight that manager.
      </Text>
      <View>{roots.map((r) => render(r, 0))}</View>
    </Card>
  );
}
